import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { EmptyState } from "@/components/states";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { SERVICE_LABEL, type ServiceType } from "@/lib/services";
import {
  VISIT_STATUSES,
  VISIT_STATUS_CLASS,
  VISIT_STATUS_LABEL,
  VISIT_TIME_SLOTS,
  acceptVisitRequest,
  cancelVisitRequest,
  completeVisitRequest,
  effectiveSlot,
  formatVisitSlot,
  listPartnerVisitRequests,
  rescheduleVisitRequest,
  todayISO,
  type VisitRequest,
  type VisitStatus,
} from "@/lib/visits";

export const Route = createFileRoute("/_authenticated/partner/visit-requests")({
  head: () => ({
    meta: [
      { title: "Visit Requests · Landoo Partner" },
      {
        name: "description",
        content:
          "Accept, reschedule, complete or cancel customer property visit requests for your Landoo listings.",
      },
      { property: "og:title", content: "Visit Requests · Landoo Partner" },
      {
        property: "og:description",
        content: "Manage property visit requests from Landoo customers.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PartnerVisitRequestsPage,
});

type Tab = VisitStatus | "all";
const TABS: Tab[] = ["all", ...VISIT_STATUSES];

function PartnerVisitRequestsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [mode, setMode] = useState<"accept" | "reschedule" | "cancel">("accept");
  const [note, setNote] = useState("");
  const [rDate, setRDate] = useState(todayISO());
  const [rTime, setRTime] = useState<string>(VISIT_TIME_SLOTS[1]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["partner", "visit-requests", user?.id],
    queryFn: () => listPartnerVisitRequests(user!.id),
    enabled: !!user,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["partner", "visit-requests", user?.id] });

  const closeDialog = () => {
    setOpenId(null);
    setNote("");
  };

  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Could not update the visit request.");

  const acceptM = useMutation({
    mutationFn: ({ id, note: n }: { id: string; note: string }) => acceptVisitRequest(id, n),
    onSuccess: () => {
      toast.success("Visit accepted. The customer has been notified.");
      closeDialog();
      refresh();
    },
    onError,
  });

  const rescheduleM = useMutation({
    mutationFn: ({
      id,
      date,
      time,
      note: n,
    }: {
      id: string;
      date: string;
      time: string;
      note: string;
    }) => rescheduleVisitRequest(id, date, time, n),
    onSuccess: () => {
      toast.success("New slot proposed. The customer has been notified.");
      closeDialog();
      refresh();
    },
    onError,
  });

  const cancelM = useMutation({
    mutationFn: ({ id, note: n }: { id: string; note: string }) =>
      cancelVisitRequest(id, user!.id, n),
    onSuccess: () => {
      toast.success("Visit cancelled. The customer has been notified.");
      closeDialog();
      refresh();
    },
    onError,
  });

  const completeM = useMutation({
    mutationFn: (id: string) => completeVisitRequest(id),
    onSuccess: () => {
      toast.success("Visit marked as completed.");
      refresh();
    },
    onError,
  });

  const counts = useMemo(() => {
    const map: Record<Tab, number> = { all: rows.length } as Record<Tab, number>;
    for (const s of VISIT_STATUSES) map[s] = rows.filter((r) => r.status === s).length;
    return map;
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== "all" && r.status !== tab) return false;
      if (!q) return true;
      return (
        (r.customer_name ?? "").toLowerCase().includes(q) ||
        (r.listing_title ?? "").toLowerCase().includes(q) ||
        (r.customer_contact ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, tab, search]);

  const active = rows.find((r) => r.id === openId) ?? null;

  return (
    <DashboardShell title="Visit Requests" role="Partner" items={PARTNER_NAV}>
      <div className="space-y-4">
        <header className="rounded-3xl border border-border bg-card p-4 shadow-soft">
          <h1 className="text-lg font-bold text-brown-dark">Property visit requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Accept a slot, propose a new one, mark visits completed or cancel. The customer is
            notified after every update.
          </p>
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by customer, listing or mobile"
              className="pl-9"
            />
          </div>
        </header>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                tab === t
                  ? "bg-brown-dark text-primary-foreground"
                  : "border border-border bg-card text-brown-dark hover:bg-secondary"
              }`}
            >
              {t === "all" ? "All" : VISIT_STATUS_LABEL[t]} ({counts[t] ?? 0})
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading visit requests…
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            title="No visit requests"
            description="When customers request a property visit, it will show up here."
          />
        ) : (
          <ul className="space-y-3">
            {visible.map((v) => (
              <li key={v.id} className="rounded-3xl border border-border bg-card p-4 shadow-soft">
                <VisitRow
                  visit={v}
                  onAction={(m) => {
                    setOpenId(v.id);
                    setMode(m);
                    setNote("");
                    setRDate(v.preferred_date >= todayISO() ? v.preferred_date : todayISO());
                    setRTime(v.preferred_time);
                  }}
                  onComplete={() => completeM.mutate(v.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-brown-dark/40 p-0 sm:items-center sm:p-6">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={
              mode === "accept"
                ? "Accept visit"
                : mode === "reschedule"
                  ? "Reschedule visit"
                  : "Cancel visit"
            }
            className="w-full max-w-md rounded-t-3xl border border-border bg-card p-4 shadow-soft sm:rounded-3xl"
          >
            <h2 className="text-base font-bold text-brown-dark">
              {mode === "accept"
                ? "Accept visit"
                : mode === "reschedule"
                  ? "Propose a new slot"
                  : "Cancel visit"}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {active.listing_title ?? "Listing"} · {formatVisitSlot(...slotTuple(active))}
            </p>

            {mode === "reschedule" && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-brown-dark">New date</span>
                  <Input
                    type="date"
                    min={todayISO()}
                    value={rDate}
                    onChange={(e) => setRDate(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-brown-dark">New time</span>
                  <select
                    value={rTime}
                    onChange={(e) => setRTime(e.target.value)}
                    className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-brown-dark"
                  >
                    {VISIT_TIME_SLOTS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold text-brown-dark">
                Note to customer (optional)
              </span>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </label>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={closeDialog}
                className="flex-1 rounded-full border border-border px-4 py-2 text-sm font-semibold text-brown-dark transition hover:bg-secondary"
              >
                Close
              </button>
              <button
                type="button"
                disabled={acceptM.isPending || rescheduleM.isPending || cancelM.isPending}
                onClick={() => {
                  if (mode === "accept") acceptM.mutate({ id: active.id, note });
                  else if (mode === "reschedule")
                    rescheduleM.mutate({ id: active.id, date: rDate, time: rTime, note });
                  else cancelM.mutate({ id: active.id, note });
                }}
                className="flex-1 rounded-full bg-brown-dark px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
              >
                {mode === "accept"
                  ? "Accept"
                  : mode === "reschedule"
                    ? "Send new slot"
                    : "Cancel visit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function slotTuple(v: VisitRequest): [string, string] {
  const s = effectiveSlot(v);
  return [s.date, s.time];
}

function VisitRow({
  visit,
  onAction,
  onComplete,
}: {
  visit: VisitRequest;
  onAction: (mode: "accept" | "reschedule" | "cancel") => void;
  onComplete: () => void;
}) {
  const open =
    visit.status === "pending" || visit.status === "accepted" || visit.status === "rescheduled";
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-brown-dark">
            {visit.listing_title ?? "Listing"}
          </div>
          <div className="text-xs text-muted-foreground">
            {SERVICE_LABEL[visit.service_type as ServiceType] ?? visit.service_type} ·{" "}
            {visit.customer_name ?? "Customer"}
            {visit.customer_contact ? ` · ${visit.customer_contact}` : ""}
          </div>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${VISIT_STATUS_CLASS[visit.status]}`}
        >
          {VISIT_STATUS_LABEL[visit.status]}
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <Info
          label="Requested slot"
          value={formatVisitSlot(visit.preferred_date, visit.preferred_time)}
        />
        {visit.proposed_date && (
          <Info
            label="Proposed slot"
            value={formatVisitSlot(visit.proposed_date, visit.proposed_time)}
          />
        )}
      </div>

      {visit.message && (
        <p className="rounded-2xl bg-secondary px-3 py-2 text-xs text-brown-dark">
          {visit.message}
        </p>
      )}
      {visit.partner_note && (
        <p className="text-xs text-muted-foreground">Your note: {visit.partner_note}</p>
      )}

      {open && (
        <div className="flex flex-wrap gap-2">
          {visit.status !== "accepted" && (
            <Action label="Accept" onClick={() => onAction("accept")} primary />
          )}
          <Action label="Reschedule" onClick={() => onAction("reschedule")} />
          {visit.status === "accepted" && <Action label="Mark completed" onClick={onComplete} />}
          <Action label="Cancel" onClick={() => onAction("cancel")} />
        </div>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-medium text-brown-dark">{value}</div>
    </div>
  );
}

function Action({
  label,
  onClick,
  primary,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
        primary
          ? "bg-brown-dark text-primary-foreground hover:opacity-90"
          : "border border-border text-brown-dark hover:bg-secondary"
      }`}
    >
      {label}
    </button>
  );
}
